import { getPersonalData } from '@api/profile';
import { isVibUser } from '@api/users';
import useToastContext from '@contexts/ToastContext/hook';
import { IMAGE_NOT_FOUND } from '@utils/imageNotFound';
import { VibDialog } from '@vibTheme/components/VibDialog';
import { Button } from '@vibTheme/components/_base/Button';
import { Image } from '@vibTheme/components/_base/Image';
import { useEffect, useState } from 'react';
import { CreateAffiliationRequestForm } from './CreateAffiliationRequestForm';
import { AffiliationMarketProductProps } from './AffiliationMarketProductCard';
import { userHasNonCompletedRequest } from '@api/affiliationRequest';
import useGroupsContext from '@contexts/GroupsContext/hook';
import { useRouter } from 'next/router';
import { AffiliationMarketProductInfo } from './AffiliationMarketProductInfo';
import { ExternalLink } from '@vibTheme/components/_base/ExternalLink';
import { VerifyUserTelegramWidget } from '@components/Telegram/VerifyUserWidget';

export const AffiliationMarketProductPage = (
  props: AffiliationMarketProductProps,
) => {
  const { product } = props;

  const [showTelegramAuth, setShowTelegramAuth] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [hasVibUser, setHasVibUser] = useState(false);
  const [affiliationStatus, setAffiliationStatus] = useState<
    'requested' | 'affiliated' | 'owner'
  >();

  const { groups } = useGroupsContext();
  const toast = useToastContext();
  const router = useRouter();

  const handleHasVibUser = async () => {
    try {
      const user = await getPersonalData();
      if (!user)
        throw new Error('Ocorreu um erro ao buscar os dados do usuário');
      const { status } = await isVibUser(user);
      setHasVibUser(status);
    } catch (err) {
      toast.handleError(err, 'Ocorreu um erro ao verificar o usuário');
    }
  };

  const handleAffiliationStatus = async () => {
    const affiliated = groups.affiliated.some(
      (group) => group.id === product.botConfigId,
    );
    const owner = groups.owner.some(
      (group) => group.id === product.botConfigId,
    );
    const requested = await userHasNonCompletedRequest(product.botConfigId);

    if (owner) setAffiliationStatus('owner');
    else if (affiliated) setAffiliationStatus('affiliated');
    else if (requested) setAffiliationStatus('requested');
  };

  useEffect(() => {
    handleHasVibUser();
    handleAffiliationStatus();
  }, []);

  const handleShowForm = async () => {
    setShowTelegramAuth(false);
    setIsFormVisible(true);
  };

  const handleCtaClick = () => {
    switch (affiliationStatus) {
      case 'owner':
      case 'affiliated':
        router.push(`/products/${product.productCode}/outreachLinks`);
        break;
      case 'requested':
        router.push(`/affiliation/affiliationRequests`);
        break;
      default:
        if (hasVibUser) handleShowForm();
        else setShowTelegramAuth(true);
    }
  };

  const resolveAffiliationButtonText = () => {
    switch (affiliationStatus) {
      case 'affiliated':
        return 'Você já é afiliado! Comece a vender!';

      case 'requested':
        return 'Você já solicitou afiliação a esta comunidade';

      case 'owner':
        return 'Essa comunidade é sua! Comece a vender!';

      default:
        return 'Quero me afiliar';
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-x-md mt-main">
      <VibDialog
        title="Entrar com o Telegram"
        open={showTelegramAuth}
        setIsOpen={setShowTelegramAuth}
        confirmBtnText="Avançar"
        content={<VerifyUserTelegramWidget handleNext={handleShowForm} />}
      />
      {isFormVisible ? (
        <CreateAffiliationRequestForm product={product} />
      ) : (
        <>
          <div className="relative w-full md:w-1/3 h-[16rem] rounded-default">
            <Image
              src={product.image || IMAGE_NOT_FOUND}
              alt={`Imagem de ${product.name}`}
              className={`${product.image ? 'object-cover' : 'object-contain'}`}
              fill
            />
          </div>

          <div className="flex flex-col justify-between w-full pt-sm sm:pt-0">
            <AffiliationMarketProductInfo
              descriptionLabel="Por que deve se afiliar a essa comunidade?"
              product={product}
            />
            <div className="flex flex-col items-center">
              <Button
                onClick={handleCtaClick}
                disabled={affiliationStatus === 'requested'}
                className="w-full text-lg rounded-default mt-md sm:mt-0 mb-sm"
              >
                {resolveAffiliationButtonText()}
              </Button>
              <ExternalLink
                url={product.marketplaceUrl}
                text="Conheça a Comunidade"
                containerClassName="pt-sm"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
